/**
 * auto-captions sound cues — AI-detected bracketed sound events in captions.
 *
 *   [applause]  [laughter]  [music]  [cheering]  [silence]
 *
 * What this is:
 *   YAMNet (TF.js) — a 521-class AudioSet classifier (Google, MIT licensed)
 *   that takes 0.96 s windows of 16 kHz mono PCM and returns class
 *   probabilities. We only consume a handful of accessibility-relevant
 *   classes (applause / laughter / music / cheering / crowd / silence)
 *   and emit them as bracketed pseudo-words spliced into the transcript
 *   at the correct timestamps. Screen readers get the same line via the
 *   blade's live region (#ac-sr-captions) so a deaf or blind viewer
 *   hears "applause" announced where it actually happens in the video.
 *
 * Why "non-speech windows only":
 *   YAMNet on a speech-overlapped window almost always returns "Speech"
 *   with high confidence and buries laughter / applause behind it. By
 *   running ONLY on gaps between VAD speech segments (or wholesale on
 *   short clips when VAD isn't available) we sidestep the ranking
 *   problem entirely. Cost: short laughs that overlap dialogue won't
 *   surface — acceptable, the alternative is a flood of false [music]
 *   on every long monologue with light underscore.
 *
 * Why a separate module:
 *   YAMNet weights are ~17 MB and we don't want them on every visitor's
 *   first paint. The module lazy-loads the TF.js runtime + weights on
 *   the FIRST flip of the toggle (isEnabled() reflects the toggle).
 *   Subsequent runs reuse the in-memory model.
 *
 * API surface:
 *   window.__acSoundCues = {
 *       isEnabled(): bool         — current toggle state (default off).
 *       setEnabled(on): void      — flip toggle. First flip-on lazy-loads model.
 *       detect(pcmF32, sampleRate, vadSegments)
 *           -> Promise<Array<{start, end, label, confidence}>>
 *           — runs on non-speech windows > 0.4 s. Empty array if
 *             nothing detected or model unavailable.
 *       injectIntoTranscript(words, cues) -> Array<word>
 *           — returns a NEW word array with bracketed pseudo-words
 *             spliced in at the right times. Each cue is one word with
 *             { text: '[applause]', start, end, isSoundCue: true,
 *               cueLabel: 'applause', cueConfidence: 0.83 }.
 *       reset(): void             — drops the model from memory.
 *   }
 *
 * Testability:
 *   tests/js/auto-captions-sound-cues.test.mjs stubs the YAMNet runner
 *   via window.__acSoundCuesTestStub = function(pcm, sr) -> [...cues]
 *   so the unit tests don't need TF.js or any model weights.
 */
(function(){
    'use strict';

    var MODEL_URL = 'https://tfhub.dev/google/tfjs-model/yamnet/tfjs/1';
    var SAMPLE_RATE = 16000;       // YAMNet expects 16 kHz mono
    var WINDOW_SEC = 0.96;         // YAMNet's native input window
    var MIN_GAP_SEC = 0.4;         // skip non-speech gaps shorter than this
    var CONF_THRESHOLD = 0.35;     // probability cutoff for emitting a cue

    /* AudioSet class IDs we care about. The full ontology has 521 classes;
       these are the ones that meaningfully improve accessibility when
       surfaced in captions. Keys are HUMAN labels for the bracket; values
       are the YAMNet class indices (subset of the AudioSet ontology). */
    var CUE_CLASSES = {
        applause:  [1, 30],            // Applause / Hands
        laughter:  [16, 17, 18],       // Laughter / Baby laughter / Giggle
        music:     [132, 137, 138],    // Music / Musical instrument / Plucked
        cheering:  [3, 8],             // Cheering / Crowd
        crowd:     [4, 5],             // Crowd / Hubbub
    };
    // Internal "preferred" order — first match wins if multiple classes
    // score above threshold in one window. Applause / laughter take
    // precedence over generic music because they're more informative.
    var CUE_PRIORITY = ['laughter', 'applause', 'cheering', 'crowd', 'music'];

    var _enabled = false;
    var _model = null;            // resolved TF.js model or null
    var _modelLoading = null;     // in-flight Promise dedup
    var _tfReady = null;

    function isEnabled() { return !!_enabled; }

    function setEnabled(on) {
        _enabled = !!on;
        // No eager load here — detect() handles lazy load. Setting to
        // true repeatedly doesn't spam parallel loads (dedup via _modelLoading).
    }

    function reset() {
        _model = null;
        _modelLoading = null;
        _tfReady = null;
        // Toggle stays as-is. Caller decides whether to flip it back.
    }

    /* ───────────────────────────────────────────────────────────
       Lazy load TF.js + YAMNet. Idempotent.
       The test harness stubs the loader by setting
         window.__acSoundCuesTestStub
       which short-circuits this whole branch — production never sets it.
       ─────────────────────────────────────────────────────────── */
    function loadModel(onProgress) {
        if (typeof window.__acSoundCuesTestStub === 'function') {
            // Tests: model is the stub itself. Mark loaded.
            _model = { _isStub: true };
            return Promise.resolve(_model);
        }
        if (_model) return Promise.resolve(_model);
        if (_modelLoading) return _modelLoading;

        _modelLoading = (async function(){
            // Step 1 — TF.js runtime. We pull from the same /cdn proxy
            // pattern other tools use so the bytes stay cached behind our edge.
            if (!window.tf) {
                if (typeof onProgress === 'function') onProgress({ phase: 'tfjs', progress: 0 });
                await loadScript('/cdn/jsdelivr/npm/@tensorflow/tfjs@4.21.0/dist/tf.min.js');
            }
            if (typeof onProgress === 'function') onProgress({ phase: 'tfjs', progress: 1 });

            // Step 2 — YAMNet weights. graphModel format, ~17 MB.
            if (typeof onProgress === 'function') onProgress({ phase: 'yamnet', progress: 0 });
            var model = await window.tf.loadGraphModel(MODEL_URL, { fromTFHub: true });
            if (typeof onProgress === 'function') onProgress({ phase: 'yamnet', progress: 1 });
            _model = model;
            return model;
        })().catch(function(err){
            _modelLoading = null;
            throw err;
        });

        return _modelLoading;
    }

    function loadScript(src) {
        return new Promise(function(resolve, reject){
            var s = document.createElement('script');
            s.src = src;
            s.async = true;
            s.onload = function(){ resolve(); };
            s.onerror = function(){ reject(new Error('Failed to load ' + src)); };
            document.head.appendChild(s);
        });
    }

    /* ───────────────────────────────────────────────────────────
       Build the list of non-speech windows from VAD output.

       Input: total audio duration in seconds + vad segments
         [{ start, end }] of SPEECH regions.
       Output: array of NON-SPEECH gaps [{ start, end }] each ≥ MIN_GAP_SEC.

       When vadSegments is empty/missing, we treat the WHOLE clip as one
       non-speech window — that's correct for music videos / sound effects
       reels / silent intros that VAD skipped over.
       ─────────────────────────────────────────────────────────── */
    function buildGaps(totalDurSec, vadSegments) {
        var gaps = [];
        if (!Array.isArray(vadSegments) || !vadSegments.length) {
            if (totalDurSec >= MIN_GAP_SEC) {
                gaps.push({ start: 0, end: totalDurSec });
            }
            return gaps;
        }
        var sorted = vadSegments.slice().sort(function(a, b){ return a.start - b.start; });
        var cursor = 0;
        for (var i = 0; i < sorted.length; i++) {
            var seg = sorted[i];
            if (seg.start - cursor >= MIN_GAP_SEC) {
                gaps.push({ start: cursor, end: seg.start });
            }
            cursor = Math.max(cursor, seg.end);
        }
        if (totalDurSec - cursor >= MIN_GAP_SEC) {
            gaps.push({ start: cursor, end: totalDurSec });
        }
        return gaps;
    }

    /* Slice a gap into ≤ WINDOW_SEC sub-windows; each becomes one YAMNet
       forward pass. Returns array of { start, end, samples (Float32Array) }. */
    function sliceGap(pcm, sampleRate, gap) {
        var windows = [];
        var t = gap.start;
        while (t + MIN_GAP_SEC <= gap.end) {
            var winEnd = Math.min(gap.end, t + WINDOW_SEC);
            var s0 = Math.max(0, Math.floor(t * sampleRate));
            var s1 = Math.min(pcm.length, Math.ceil(winEnd * sampleRate));
            if (s1 - s0 >= Math.floor(sampleRate * MIN_GAP_SEC)) {
                windows.push({
                    start: t,
                    end: winEnd,
                    samples: pcm.subarray(s0, s1),
                });
            }
            t = winEnd;
        }
        return windows;
    }

    /* Run YAMNet (or stub) on one Float32 window → top cue or null.
       Returns null when nothing crosses CONF_THRESHOLD. */
    async function classifyWindow(samples, sampleRate) {
        if (typeof window.__acSoundCuesTestStub === 'function') {
            // Stub returns one of:
            //   - null / undefined  → no cue
            //   - { label, confidence }
            var stubResult = window.__acSoundCuesTestStub(samples, sampleRate);
            if (!stubResult) return null;
            if (stubResult.confidence == null || stubResult.confidence < CONF_THRESHOLD) return null;
            return { label: stubResult.label, confidence: stubResult.confidence };
        }
        if (!_model || !window.tf) return null;
        // YAMNet expects a 1-D Float32 tensor of waveform samples.
        var input = window.tf.tensor(samples, [samples.length], 'float32');
        try {
            var output = _model.execute(input);
            // The graph returns [scores, embeddings, log_mel]. Scores shape:
            // [num_frames, 521]. We mean-pool across frames to get one
            // 521-vector for the whole window.
            var scores = Array.isArray(output) ? output[0] : output;
            var meanScores = window.tf.tidy(function(){
                return window.tf.mean(scores, 0);
            });
            var probsArr = await meanScores.data();
            meanScores.dispose();
            if (Array.isArray(output)) {
                for (var oi = 0; oi < output.length; oi++) output[oi].dispose();
            } else if (output && output.dispose) {
                output.dispose();
            }
            return pickTopCue(probsArr);
        } finally {
            input.dispose();
        }
    }

    /* Given the full 521-class probability vector, pick the highest-scoring
       cue we care about. Returns { label, confidence } or null. */
    function pickTopCue(probs) {
        var best = null;
        for (var pi = 0; pi < CUE_PRIORITY.length; pi++) {
            var label = CUE_PRIORITY[pi];
            var classIds = CUE_CLASSES[label] || [];
            var maxProb = 0;
            for (var ci = 0; ci < classIds.length; ci++) {
                var p = probs[classIds[ci]] || 0;
                if (p > maxProb) maxProb = p;
            }
            if (maxProb >= CONF_THRESHOLD && (!best || maxProb > best.confidence)) {
                best = { label: label, confidence: maxProb };
            }
        }
        return best;
    }

    /* Merge adjacent cues with the SAME label into one event. Two
       0.96 s "applause" windows back-to-back should read as one
       [applause] of 1.9 s, not two. */
    function coalesce(cues) {
        if (!cues.length) return cues;
        var out = [cues[0]];
        for (var i = 1; i < cues.length; i++) {
            var prev = out[out.length - 1];
            var cur = cues[i];
            if (cur.label === prev.label && cur.start - prev.end < 0.3) {
                prev.end = cur.end;
                prev.confidence = Math.max(prev.confidence, cur.confidence);
            } else {
                out.push(cur);
            }
        }
        return out;
    }

    /* ───────────────────────────────────────────────────────────
       Public: detect cues on non-speech windows.
       ─────────────────────────────────────────────────────────── */
    async function detect(pcmF32, sampleRate, vadSegments) {
        if (!_enabled) return [];
        if (!pcmF32 || !pcmF32.length) return [];
        sampleRate = sampleRate || SAMPLE_RATE;
        var totalDurSec = pcmF32.length / sampleRate;

        // Lazy-load model on first detect() call after toggle flip.
        try {
            await loadModel();
        } catch (err) {
            console.warn('[auto-captions] sound-cues model load failed:', err && err.message);
            return [];
        }

        var gaps = buildGaps(totalDurSec, vadSegments);
        if (!gaps.length) return [];

        var cues = [];
        for (var g = 0; g < gaps.length; g++) {
            var windows = sliceGap(pcmF32, sampleRate, gaps[g]);
            for (var w = 0; w < windows.length; w++) {
                var win = windows[w];
                var picked;
                try {
                    picked = await classifyWindow(win.samples, sampleRate);
                } catch (err) {
                    console.warn('[auto-captions] sound-cues classify failed at ' + win.start.toFixed(2) + 's:', err && err.message);
                    continue;
                }
                if (picked) {
                    cues.push({
                        start: win.start,
                        end: win.end,
                        label: picked.label,
                        confidence: picked.confidence,
                    });
                }
            }
        }
        return coalesce(cues);
    }

    /* ───────────────────────────────────────────────────────────
       Public: splice cues into a words array as bracketed pseudo-words.

       Strategy: cues are inserted at the first index where the existing
       word's start time exceeds the cue's start. Each cue becomes one
       pseudo-word { text: '[label]', start, end, isSoundCue: true,
       cueLabel, cueConfidence }.

       Returns a NEW array — caller's array is not mutated.
       ─────────────────────────────────────────────────────────── */
    function injectIntoTranscript(words, cues) {
        if (!Array.isArray(words)) return [];
        if (!Array.isArray(cues) || !cues.length) return words.slice();
        var result = words.slice();
        // Insert in time-descending order so later inserts don't shift
        // the index of earlier ones — but since we want a stable forward
        // iteration we instead build a sorted merge below.
        var pseudoWords = [];
        for (var i = 0; i < cues.length; i++) {
            var c = cues[i];
            pseudoWords.push({
                text: '[' + c.label + ']',
                start: c.start,
                end: c.end,
                isSoundCue: true,
                cueLabel: c.label,
                cueConfidence: c.confidence,
            });
        }
        // Merge result + pseudoWords sorted by start time. Stable on ties:
        // existing speech word wins (so [music] inserted mid-word sits
        // before the next word, not in the middle of the current one).
        var merged = result.concat(pseudoWords);
        merged.sort(function(a, b){
            if (a.start === b.start) {
                // Sound cue goes after real word at exact tie
                if (a.isSoundCue && !b.isSoundCue) return 1;
                if (!a.isSoundCue && b.isSoundCue) return -1;
                return 0;
            }
            return a.start - b.start;
        });
        return merged;
    }

    /* Expose. */
    window.__acSoundCues = {
        isEnabled: isEnabled,
        setEnabled: setEnabled,
        detect: detect,
        injectIntoTranscript: injectIntoTranscript,
        reset: reset,
        // Exposed for tests:
        _buildGaps: buildGaps,
        _coalesce: coalesce,
        _pickTopCue: pickTopCue,
        _CONF_THRESHOLD: CONF_THRESHOLD,
        _CUE_CLASSES: CUE_CLASSES,
    };
})();
